import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import PageHeader, { KpiTile } from '../components/common/PageHeader';
import ResponsiveTable from '../components/common/ResponsiveTable';
import { NativeSelect, TextArea } from '../components/common/FormField';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { queryKeys } from '../lib/queryClient';
import { api, errorMessage } from '../services/api';
import { CapitalEntry, CapitalEntryType, CapitalSummary } from '../types';
import { fmtCurrency, fmtDate, PAYMENT_MODES, paymentModeLabel, todayISO, toNum } from '../utils/format';
import { compact, focusFirstError, hasErrors, serverFieldErrors, v } from '../utils/validation';

const TYPES: { value: CapitalEntryType; label: string; hint: string }[] = [
  { value: 'investment', label: 'Owner Investment', hint: 'Capital you put into the company' },
  { value: 'director_loan', label: 'Director / Owner Loan', hint: 'Money you lend to the company' },
  { value: 'loan_received', label: 'Loan Received', hint: 'Bank or third-party loan' },
  { value: 'loan_repayment', label: 'Loan Repayment', hint: 'Company repays a loan' },
  { value: 'withdrawal', label: 'Owner Withdrawal', hint: 'Money you take out' },
];
const typeLabel = (t: CapitalEntryType) => TYPES.find((x) => x.value === t)?.label ?? t;

interface FormState {
  entryDate: string;
  entryType: CapitalEntryType;
  partyName: string;
  amount: string;
  paymentMode: string;
  referenceNo: string;
  notes: string;
}

const emptyForm = (): FormState => ({
  entryDate: todayISO(), entryType: 'investment', partyName: '', amount: '', paymentMode: 'bank_transfer', referenceNo: '', notes: '',
});

export default function CapitalPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const canManage = hasPermission('capital:manage');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CapitalEntry | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.capital(),
    queryFn: () => api.get<{ items: CapitalEntry[]; summary: CapitalSummary }>('/capital'),
  });
  const items = data?.items ?? [];
  const summary = data?.summary;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.capital() });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const saveMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      editing ? api.patch(`/capital/${editing.id}`, payload) : api.post('/capital', payload),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({ mutationFn: (id: number) => api.delete(`/capital/${id}`), onSuccess: invalidate });

  function open(e?: CapitalEntry) {
    setEditing(e ?? null);
    setErrors({});
    setForm(
      e
        ? { entryDate: e.entryDate, entryType: e.entryType, partyName: e.partyName, amount: String(e.amount),
            paymentMode: e.paymentMode, referenceNo: e.referenceNo ?? '', notes: e.notes ?? '' }
        : emptyForm(),
    );
    setShowModal(true);
  }

  function setField(key: keyof FormState, value: string) {
    setForm((p) => ({ ...p, [key]: value }));
    setErrors((p) => (p[key] ? { ...p, [key]: undefined } : p));
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    const e = compact({
      entryType: form.entryType ? undefined : 'Type is required',
      entryDate: v.date(form.entryDate, 'Date', { notFuture: true }),
      amount: v.money(form.amount, 'Amount'),
      partyName: v.businessName(form.partyName, 'Person / lender', true, 150),
      paymentMode: form.paymentMode ? undefined : 'Payment mode is required',
      referenceNo: v.reference(form.referenceNo),
      notes: v.text(form.notes, 'Notes', { max: 1000 }),
    });
    setErrors(e);
    if (hasErrors(e)) return focusFirstError();
    try {
      await saveMutation.mutateAsync({
        entryDate: form.entryDate,
        entryType: form.entryType,
        partyName: form.partyName.trim(),
        amount: toNum(form.amount),
        paymentMode: form.paymentMode,
        referenceNo: form.referenceNo.trim(),
        notes: form.notes.trim(),
      });
      showToast(editing ? 'Entry updated' : 'Entry recorded', 'success');
      setShowModal(false);
    } catch (err) {
      setErrors(serverFieldErrors(err, Object.keys(form) as (keyof FormState)[]));
      showToast(errorMessage(err, 'Failed to save'), 'error');
    }
  }

  async function remove(entry: CapitalEntry) {
    const yes = await confirm({
      title: 'Delete Entry',
      message: `Delete ${typeLabel(entry.entryType)} of ${fmtCurrency(entry.amount)} dated ${fmtDate(entry.entryDate)}?`,
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!yes) return;
    try {
      await deleteMutation.mutateAsync(entry.id);
      showToast('Entry deleted', 'success');
    } catch (err) {
      showToast(errorMessage(err, 'Failed to delete'), 'error');
    }
  }

  const columns = [
    { key: 'entryDate', label: 'Date', width: '120px', render: (_: any, r: CapitalEntry) => <span className="text-sm text-ink-700">{fmtDate(r.entryDate)}</span> },
    {
      key: 'entryType',
      label: 'Type',
      render: (_: any, r: CapitalEntry) => (
        <span className={`inline-flex items-center gap-1.5 text-sm font-semibold ${r.direction > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
          <i className={`fas ${r.direction > 0 ? 'fa-arrow-down' : 'fa-arrow-up'} text-xs`} />
          {typeLabel(r.entryType)}
        </span>
      ),
    },
    { key: 'partyName', label: 'From / To', render: (_: any, r: CapitalEntry) => <span className="text-sm text-ink-800">{r.partyName}</span> },
    {
      key: 'paymentMode',
      label: 'Via',
      render: (_: any, r: CapitalEntry) => (
        <div className="text-xs text-ink-600">
          {paymentModeLabel(r.paymentMode)}
          {r.referenceNo && <span className="block font-mono text-ink-400">{r.referenceNo}</span>}
        </div>
      ),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (_: any, r: CapitalEntry) => (
        <span className={`text-sm font-bold ${r.direction > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
          {r.direction > 0 ? '+' : '−'}{fmtCurrency(r.amount)}
        </span>
      ),
    },
    ...(canManage
      ? [{
          key: 'actions',
          label: 'Actions',
          width: '150px',
          render: (_: any, r: CapitalEntry) => (
            <div className="flex items-center gap-2">
              <button type="button" className="px-3 py-1 bg-ink-100 text-ink-700 text-xs rounded hover:bg-ink-200" onClick={() => open(r)}>
                <i className="fas fa-pen mr-1" />Edit
              </button>
              <button type="button" className="px-3 py-1 bg-red-50 text-red-600 text-xs rounded hover:bg-red-100" onClick={() => remove(r)}>
                <i className="fas fa-trash-can" />
              </button>
            </div>
          ),
        }]
      : []),
  ];

  const loans = (summary?.byType.loan_received ?? 0) - (summary?.byType.loan_repayment ?? 0);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <PageHeader
          title="Capital & Investment"
          subtitle="Money you and lenders put into the company — kept separate from income and expenses"
          actions={canManage && <Button variant="primary" icon="fa-plus" onClick={() => open()}>Add Entry</Button>}
        />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 lg:mb-6">
          <KpiTile label="You Have Invested (net)" value={fmtCurrency(summary?.ownerInvested ?? 0, 0)} icon="fa-sack-dollar" tone="brand"
            hint="Investment + owner loans − withdrawals" />
          <KpiTile label="Outstanding External Loans" value={fmtCurrency(loans, 0)} icon="fa-building-columns" tone="accent" />
          <KpiTile label="Net Capital Inflow" value={fmtCurrency(summary?.netInflow ?? 0, 0)} icon="fa-right-left" tone="green" />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-ink-200 overflow-visible lg:overflow-hidden">
          <div className="px-1 py-3 lg:p-0">
            <ResponsiveTable columns={columns} data={items} isLoading={isLoading} height="calc(100vh - 340px)"
              emptyMessage="No entries yet — record your first investment into the company" />
          </div>
        </div>

        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={editing ? 'Edit Entry' : 'Add Capital Entry'}
          size="md"
          footer={
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button variant="primary" onClick={handleSubmit as any} disabled={saveMutation.isPending}>
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <NativeSelect label="Type" required value={form.entryType} error={errors.entryType} onChange={(val) => setField('entryType', val)}
                options={TYPES.map((t) => ({ value: t.value, label: t.label }))} />
              <p className="mt-1 text-xs text-ink-500">{TYPES.find((t) => t.value === form.entryType)?.hint}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input label="Date" type="date" required value={form.entryDate} max={todayISO()}
                onChange={(e) => setField('entryDate', e.target.value)} error={errors.entryDate} />
              <Input label="Amount (₹)" type="number" required value={form.amount}
                onChange={(e) => setField('amount', e.target.value)} error={errors.amount} />
              <Input label="Person / Lender" required value={form.partyName} maxLength={150}
                onChange={(e) => setField('partyName', e.target.value)} error={errors.partyName} />
              <NativeSelect label="Payment Mode" required value={form.paymentMode} error={errors.paymentMode}
                onChange={(val) => setField('paymentMode', val)} options={PAYMENT_MODES} />
            </div>
            <Input label="Reference / UTR" value={form.referenceNo} maxLength={100} error={errors.referenceNo}
              onChange={(e) => setField('referenceNo', e.target.value)} />
            <TextArea label="Notes" value={form.notes} onChange={(val) => setField('notes', val)} maxLength={1000} error={errors.notes} />
          </form>
        </Modal>

        <ConfirmDialog />
      </div>
    </Layout>
  );
}
